import React, { useRef } from 'react';
import Select, { components } from 'react-select';

import { DisplayMode } from '../ThemeContext';
import { useReactSelectSwitcher, SingleOptionType } from './useReactSelectSwitcher';
import { FaPalette } from 'react-icons/fa';

const ReactSelectSwitcher: React.FC = () => {
  const {
    selectedOption,
    options,
    displayMode,
    handleChange,
  } = useReactSelectSwitcher();

  const selectRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnOpenRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);

  const formatOptionLabel = ({ data }: SingleOptionType) => {
    const labelContent = {
      [DisplayMode.Text]: <div>{data.name}</div>,
      [DisplayMode.Icon]: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={data.iconImage}
            alt={data.name}
            style={{ height: '1em', width: '1em', marginRight: '0.5em' }}
          />
          <span>{data.name}</span>
        </div>
      ),
      [DisplayMode.Both]: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={data.iconImage}
            alt={data.name}
            style={{ height: '1em', width: '1em', marginRight: '0.5em' }}
          />
          <span>{data.name}</span>
        </div>
      ),
    };

    return labelContent[displayMode] || <div>{data.name}</div>;
  };

  const clearPendingAutoScroll = () => {
    if (scrollTimeoutRef.current !== null) {
      window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  };

  const scrollToSelectedOptionOnce = () => {
    if (hasScrolledOnOpenRef.current) {
      return;
    }

    clearPendingAutoScroll();

    scrollTimeoutRef.current = window.setTimeout(() => {
      const selectedElement = document.body.querySelector(
        '.custom-option.selected, .custom-suboption.selected'
      ) as HTMLElement | null;

      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
        });
      }

      hasScrolledOnOpenRef.current = true;
      scrollTimeoutRef.current = null;
    }, 20);
  };

  const styles = {
    color: 'var(--header-link-color)',
    backgroundColor: 'var(--header-background-color)',

    option: (provided: any, state: any) => ({
      ...provided,
      fontWeight: state.isSelected ? 800 : 500,
      fontSize: '1rem',
      color: 'var(--header-link-color)',
      backgroundColor: 'var(--header-background-color)',
      borderLeft: state.isSelected
        ? '4px solid var(--header-link-color)'
        : '4px solid transparent',
      borderBottom: state.isSelected
        ? '1px solid var(--header-link-color)'
        : '1px solid transparent',
      paddingLeft: state.data?.data?.parentName ? '1.5rem' : '0.85rem',
      textDecoration: 'none',
    }),

    group: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--header-background-color)',
      marginBottom: '0.4rem',
      paddingTop: '0.25rem',
    }),

    groupHeading: (provided: any) => ({
      ...provided,
      color: 'var(--header-link-color)',
      backgroundColor: 'var(--header-background-color)',
      fontWeight: 800,
      fontSize: '1.05rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      padding: '0.55rem 0.75rem 0.45rem 0.75rem',
      marginBottom: '0.3rem',
      borderBottom: '2px solid currentColor',
    }),

    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--header-link-color)',
      backgroundColor: 'var(--header-background-color)',
      fontWeight: 700,
    }),

    control: (provided: any) => ({
      ...provided,
      border: '1px solid var(--header-link-color)',
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid var(--header-link-color)',
      },
      color: 'var(--header-link-color)',
      backgroundColor: 'var(--header-background-color)',
    }),

    menu: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--header-background-color)',
      zIndex: 3000,
    }),

    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 3000,
    }),
  };

  const CustomOption = (props: any) => {
    const { data, selectedOption } = props;

    let optionClassName = `${data.data.parentName ? 'custom-suboption' : 'custom-option'}`;
    optionClassName += selectedOption?.value === data.data.value ? ' selected' : '';

    return (
      <div className={optionClassName}>
        <components.Option {...props} />
      </div>
    );
  };

  return (
    <div className="select-wrapper" ref={selectRef}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <FaPalette size={24} className="icon-color" style={{ marginRight: '0.5em' }} />
        <Select
          value={selectedOption}
          onChange={handleChange}
          options={options}
          isOptionDisabled={(option) => !option.data.isImplemented}
          formatOptionLabel={formatOptionLabel}
          components={{
            Option: (props) => <CustomOption {...props} selectedOption={selectedOption} />
          }}
          menuPortalTarget={document.body}
          menuShouldScrollIntoView={false}
          onMenuOpen={() => {
            hasScrolledOnOpenRef.current = false;
            scrollToSelectedOptionOnce();
          }}
          onMenuClose={() => {
            hasScrolledOnOpenRef.current = false;
            clearPendingAutoScroll();
          }}
          className="custom-menu"
          styles={styles}
        />
      </div>
    </div>
  );
};

export default ReactSelectSwitcher;
